import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createSalesTrainingCompanyAPI,
  getSalesTrainingCompanyListAPI,
  getSalesTrainingCompanyByIdAPI,
  updateSalesTrainingCompanyAPI,
  deleteSalesTrainingCompanyAPI,
  archiveSalesTrainingCompanyAPI,
  addSalesTrainingSessionAPI,
  getSalesTrainingSessionsAPI,
  enableDisableSalesTrainingToolAPI,
  enableDisableSalesTrainingAssignmentAPI,
  enableDisableSalesTrainingSupportDocumentAPI,
  getSalesTrainingSessionDetailsAPI,
  enableDisableSalesTrainingSessionAPI,
  submitSalesTrainingAssignmentAPI,
  getSalesTrainingAssessmentLinkAPI,
} from "./SalesTrainingCompaniesapi";

export const createSalesTrainingCompany = createAsyncThunk(
  "salesTrainingCompanies/createSalesTrainingCompany",
  async (data, { rejectWithValue }) => {
    try {
      return await createSalesTrainingCompanyAPI(data);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to create sales training company");
    }
  }
);

export const getSalesTrainingCompanyList = createAsyncThunk(
  "salesTrainingCompanies/getSalesTrainingCompanyList",
  async ({ page = 1, limit = 10, search = "" } = {}, { rejectWithValue }) => {
    try {
      return await getSalesTrainingCompanyListAPI({ page, limit, search });
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch sales training companies");
    }
  }
);

export const getSalesTrainingCompanyById = createAsyncThunk(
  "salesTrainingCompanies/getSalesTrainingCompanyById",
  async (id, { rejectWithValue }) => {
    try {
      return await getSalesTrainingCompanyByIdAPI(id);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch sales training company detail");
    }
  }
);

export const updateSalesTrainingCompany = createAsyncThunk(
  "salesTrainingCompanies/updateSalesTrainingCompany",
  async ({ stc_id, data }, { rejectWithValue }) => {
    try {
      return await updateSalesTrainingCompanyAPI(stc_id, data);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update sales training company");
    }
  }
);

export const deleteSalesTrainingCompany = createAsyncThunk(
  "salesTrainingCompanies/deleteSalesTrainingCompany",
  async (stc_id, { rejectWithValue }) => {
    try {
      return await deleteSalesTrainingCompanyAPI(stc_id);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete sales training company");
    }
  }
);

export const archiveSalesTrainingCompany = createAsyncThunk(
  "salesTrainingCompanies/archiveSalesTrainingCompany",
  async ({ stc_id, is_archive }, { rejectWithValue }) => {
    try {
      return await archiveSalesTrainingCompanyAPI(stc_id, is_archive);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to archive sales training company");
    }
  }
);

export const addSalesTrainingSession = createAsyncThunk(
  "salesTrainingCompanies/addSalesTrainingSession",
  async (sessionData, { rejectWithValue }) => {
    try {
      return await addSalesTrainingSessionAPI(sessionData);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to add session");
    }
  }
);

export const getSalesTrainingSessions = createAsyncThunk(
  "salesTrainingCompanies/getSalesTrainingSessions",
  async ({ stc_id, page = 1, limit = 10, search = "" }, { rejectWithValue }) => {
    try {
      return await getSalesTrainingSessionsAPI({ stc_id, page, limit, search });
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch sessions");
    }
  }
);

export const enableDisableSalesTrainingTool = createAsyncThunk(
  "salesTrainingCompanies/enableDisableSalesTrainingTool",
  async (toolData, { rejectWithValue }) => {
    try {
      return await enableDisableSalesTrainingToolAPI(toolData);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update tool status");
    }
  }
);

export const enableDisableSalesTrainingAssignment = createAsyncThunk(
  "salesTrainingCompanies/enableDisableSalesTrainingAssignment",
  async (assignmentData, { rejectWithValue }) => {
    try {
      return await enableDisableSalesTrainingAssignmentAPI(assignmentData);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update assignment status");
    }
  }
);

export const enableDisableSalesTrainingSupportDocument = createAsyncThunk(
  "salesTrainingCompanies/enableDisableSalesTrainingSupportDocument",
  async (supportDocData, { rejectWithValue }) => {
    try {
      return await enableDisableSalesTrainingSupportDocumentAPI(supportDocData);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update support document status");
    }
  }
);

export const getSalesTrainingSessionDetails = createAsyncThunk(
  "salesTrainingCompanies/getSalesTrainingSessionDetails",
  async ({ sales_training_company_session_id, sales_training_company_id }, { rejectWithValue }) => {
    try {
      return await getSalesTrainingSessionDetailsAPI(sales_training_company_session_id, sales_training_company_id);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch session details");
    }
  }
);

export const enableDisableSalesTrainingSession = createAsyncThunk(
  "salesTrainingCompanies/enableDisableSalesTrainingSession",
  async ({ sales_training_company_id, sales_training_company_session_id, assign_status, mark_completed = false }, { rejectWithValue }) => {
    try {
      return await enableDisableSalesTrainingSessionAPI({ sales_training_company_id, sales_training_company_session_id, assign_status, mark_completed });
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to enable/disable sales training session");
    }
  }
);

export const submitSalesTrainingAssignment = createAsyncThunk(
  "salesTrainingCompanies/submitSalesTrainingAssignment",
  async ({ session_assignment_id, answers }, { rejectWithValue }) => {
    try {
      return await submitSalesTrainingAssignmentAPI({ session_assignment_id, answers });
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to submit assignment");
    }
  }
);

export const getSalesTrainingAssessmentLink = createAsyncThunk(
  "salesTrainingCompanies/getSalesTrainingAssessmentLink",
  async ({ sales_training_company_session_id, sales_training_company_id }, { rejectWithValue }) => {
    try {
      return await getSalesTrainingAssessmentLinkAPI(sales_training_company_session_id, sales_training_company_id);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch assessment link");
    }
  }
);

const initialState = {
  createSalesTrainingCompany:           { data: null, loading: false, error: null },
  getSalesTrainingCompanyList:          { data: [], total: 0, loading: false, error: null },
  getSalesTrainingCompanyById:          { data: null, loading: false, error: null },
  updateSalesTrainingCompany:           { data: null, loading: false, error: null },
  deleteSalesTrainingCompany:           { data: null, loading: false, error: null },
  archiveSalesTrainingCompany:          { data: null, loading: false, error: null },
  addSalesTrainingSession:              { data: null, loading: false, error: null },
  getSalesTrainingSessions:             { data: [], total: 0, loading: false, error: null },
  enableDisableSalesTrainingTool:       { data: null, loading: false, error: null },
  enableDisableSalesTrainingAssignment: { data: null, loading: false, error: null },
  enableDisableSalesTrainingSupportDocument: { data: null, loading: false, error: null },
  getSalesTrainingSessionDetails:       { data: null, loading: false, error: null },
  enableDisableSalesTrainingSession:    { data: null, loading: false, error: null },
  submitSalesTrainingAssignment:        { data: null, loading: false, error: null },
  getSalesTrainingAssessmentLink:       { data: null, loading: false, error: null },
};

const buildAsyncReducers = (builder, thunk, stateKey, isListWithTotal = false) => {
  builder
    .addCase(thunk.pending, (state) => {
      state[stateKey].loading = true;
      state[stateKey].error = null;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state[stateKey].loading = false;
      if (isListWithTotal) {
        state[stateKey].data = action.payload?.data || [];
        state[stateKey].total = action.payload?.total || 0;
      } else {
        state[stateKey].data = action.payload?.data || action.payload;
      }
    })
    .addCase(thunk.rejected, (state, action) => {
      state[stateKey].loading = false;
      state[stateKey].error = action.payload;
    });
};

const salesTrainingCompaniesSlice = createSlice({
  name: "salesTrainingCompanies",
  initialState,
  reducers: {
    clearCreateSalesTrainingCompany: (state) => { state.createSalesTrainingCompany = { data: null, loading: false, error: null }; },
    clearSalesTrainingCompanyList: (state) => { state.getSalesTrainingCompanyList = { data: [], total: 0, loading: false, error: null }; },
    clearSalesTrainingCompanyById: (state) => { state.getSalesTrainingCompanyById = { data: null, loading: false, error: null }; },
    clearUpdateSalesTrainingCompany: (state) => { state.updateSalesTrainingCompany = { data: null, loading: false, error: null }; },
    clearDeleteSalesTrainingCompany: (state) => { state.deleteSalesTrainingCompany = { data: null, loading: false, error: null }; },
    clearArchiveSalesTrainingCompany: (state) => { state.archiveSalesTrainingCompany = { data: null, loading: false, error: null }; },
    clearAddSalesTrainingSession: (state) => { state.addSalesTrainingSession = { data: null, loading: false, error: null }; },
    clearSalesTrainingSessions: (state) => { state.getSalesTrainingSessions = { data: [], total: 0, loading: false, error: null }; },
    clearSalesTrainingSessionDetails: (state) => { state.getSalesTrainingSessionDetails = { data: null, loading: false, error: null }; },
    clearEnableDisableSalesTrainingSession: (state) => { state.enableDisableSalesTrainingSession = { data: null, loading: false, error: null }; },
    clearSubmitSalesTrainingAssignment: (state) => { state.submitSalesTrainingAssignment = { data: null, loading: false, error: null }; },
    clearSalesTrainingAssessmentLink: (state) => { state.getSalesTrainingAssessmentLink = { data: null, loading: false, error: null }; },
  },
  extraReducers: (builder) => {
    buildAsyncReducers(builder, createSalesTrainingCompany, 'createSalesTrainingCompany');
    buildAsyncReducers(builder, getSalesTrainingCompanyList, 'getSalesTrainingCompanyList', true);
    buildAsyncReducers(builder, getSalesTrainingCompanyById, 'getSalesTrainingCompanyById');
    buildAsyncReducers(builder, updateSalesTrainingCompany, 'updateSalesTrainingCompany');
    buildAsyncReducers(builder, deleteSalesTrainingCompany, 'deleteSalesTrainingCompany');
    buildAsyncReducers(builder, archiveSalesTrainingCompany, 'archiveSalesTrainingCompany');
    buildAsyncReducers(builder, addSalesTrainingSession, 'addSalesTrainingSession');
    buildAsyncReducers(builder, getSalesTrainingSessions, 'getSalesTrainingSessions', true);
    buildAsyncReducers(builder, enableDisableSalesTrainingTool, 'enableDisableSalesTrainingTool');
    buildAsyncReducers(builder, enableDisableSalesTrainingAssignment, 'enableDisableSalesTrainingAssignment');
    buildAsyncReducers(builder, enableDisableSalesTrainingSupportDocument, 'enableDisableSalesTrainingSupportDocument');
    buildAsyncReducers(builder, getSalesTrainingSessionDetails, 'getSalesTrainingSessionDetails');
    buildAsyncReducers(builder, enableDisableSalesTrainingSession, 'enableDisableSalesTrainingSession');
    buildAsyncReducers(builder, submitSalesTrainingAssignment, 'submitSalesTrainingAssignment');
    buildAsyncReducers(builder, getSalesTrainingAssessmentLink, 'getSalesTrainingAssessmentLink');
  },
});

export const {
  clearCreateSalesTrainingCompany,
  clearSalesTrainingCompanyList,
  clearSalesTrainingCompanyById,
  clearUpdateSalesTrainingCompany,
  clearDeleteSalesTrainingCompany,
  clearArchiveSalesTrainingCompany,
  clearAddSalesTrainingSession,
  clearSalesTrainingSessions,
  clearSalesTrainingSessionDetails,
  clearEnableDisableSalesTrainingSession,
  clearSubmitSalesTrainingAssignment,
  clearSalesTrainingAssessmentLink,
} = salesTrainingCompaniesSlice.actions;

export default salesTrainingCompaniesSlice.reducer;
